import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Filter, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import type { CalendarFilters } from '../hooks/useCalendarFilters';

interface CalendarFilterBarProps {
  filters: CalendarFilters;
  updateFilter: <K extends keyof CalendarFilters>(key: K, value: CalendarFilters[K]) => void;
  resetFilters: () => void;
}

export function CalendarFilterBar({ filters, updateFilter, resetFilters }: CalendarFilterBarProps) {
  const { t } = useTranslation();
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.createdById) count++;
    if (filters.attendeeId) count++;
    if (filters.clientId) count++;
    if (filters.matterId) count++;
    if (filters.target) count++;
    return count;
  }, [filters]);

  return (
    <div className="bg-[#f8fafc] border-b border-[#e2e8f0]">
      {/* Collapsed Filter Bar */}
      <div
        className="flex items-center justify-between px-6 py-3 cursor-pointer hover:bg-[#f1f5f9] transition-colors"
        role="button"
        tabIndex={0}
        onClick={() => setFiltersExpanded(!filtersExpanded)}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setFiltersExpanded(!filtersExpanded)}
      >
        <div className="flex items-center gap-3">
          <Filter className="h-4 w-4 text-[#64748b]" />
          <span className="text-sm font-medium text-[#475569]">{t('actions.filter')}</span>
          {activeFilterCount > 0 && (
            <Badge
              variant="secondary"
              className="bg-[#1a2b4a] text-white text-xs px-2 py-0.5 rounded-full"
            >
              {activeFilterCount}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          {activeFilterCount > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                resetFilters();
              }}
              className="text-xs text-[#64748b] hover:text-[#1a2b4a] font-medium flex items-center gap-1"
            >
              <X className="h-3 w-3" />
              {t('clearFilters')}
            </button>
          )}
          {filtersExpanded ? (
            <ChevronUp className="h-4 w-4 text-[#64748b]" />
          ) : (
            <ChevronDown className="h-4 w-4 text-[#64748b]" />
          )}
        </div>
      </div>

      {/* Expanded Filters */}
      {filtersExpanded && (
        <div className="px-6 pb-4 pt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 bg-white border-t border-[#e2e8f0]">
          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
              {t('createdBy')}
            </label>
            <UniversalCombobox
              url="/users"
              listUrl="/users/select-box"
              labelKey="name"
              valueKey="id"
              selectedId={filters.createdById}
              onSelectItem={(user) => updateFilter('createdById', user ? user.id : undefined)}
              placeholder={t('all')}
              className="w-full"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
              {t('attendees')}
            </label>
            <UniversalCombobox
              url="/users"
              listUrl="/users/select-box"
              labelKey="name"
              valueKey="id"
              selectedId={filters.attendeeId}
              onSelectItem={(user) => updateFilter('attendeeId', user ? user.id : undefined)}
              placeholder={t('selectAttendee')}
              className="w-full"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
              {t('client')}
            </label>
            <UniversalCombobox
              url="/clients"
              labelKey="name"
              labelKeys={['lastname', 'name', 'middlename']}
              valueKey="id"
              selectedId={filters.clientId}
              onSelectItem={(client) => updateFilter('clientId', client ? client.id : undefined)}
              placeholder={t('selectClient')}
              className="w-full"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
              {t('matter')}
            </label>
            <UniversalCombobox
              url="/matters"
              listUrl="/matters/select-box"
              labelKey="name"
              valueKey="id"
              selectedId={filters.matterId}
              params={{ clientId: filters.clientId || undefined }}
              onSelectItem={(matter) => updateFilter('matterId', matter ? matter.id : undefined)}
              placeholder={t('selectMatter')}
              className="w-full"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
              {t('eventType')}
            </label>
            <Select
              value={filters.target ?? 'none'}
              onValueChange={(val) => updateFilter('target', val === 'none' ? undefined : val)}
            >
              <SelectTrigger className="w-full h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8] focus:border-[#1a2b4a] focus:ring-[#1a2b4a]/10">
                <SelectValue placeholder={t('target.target')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('all')}</SelectItem>
                <SelectItem value="FIRM_EVENT">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    {t('target.FIRM_EVENT')}
                  </div>
                </SelectItem>
                <SelectItem value="NEW_CLIENT">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-sky-500" />
                    {t('target.NEW_CLIENT')}
                  </div>
                </SelectItem>
                <SelectItem value="CASE_MEETING">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-violet-500" />
                    {t('target.CASE_MEETING')}
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
}
